import React, { useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  Popover,
  PopoverContent,
  PopoverTrigger
} from "@/components/ui/popover";
import { Filter } from "@dreamhub/designsystem/icons";
import HeaderAction from "@/components/controls/DataTable/TableHeader/HeaderAction";
import FilterContent from "@/components/controls/ToolbarActions/ToolbarFilterButton/FilterContent";
import { DataTableFilter } from "@/components/controls/DataTableFilters/DataTableFilters";
import { Filters, FiltersSetter } from "@/services/api/types";
import { cn } from "@/utils/utils";
import { extractFilterValues, getFilterLabel } from "@/hooks/useFilterOptions";
import { Button } from "@/components/ui/button";
import Text from "@/components/ui/text";

interface Props {
  filterKey: string;
  filterOptions: DataTableFilter[];
  filters: Filters;
  setFilters: FiltersSetter;
  isHovered: boolean;
}

const HeaderFilterPopover: React.FC<Props> = ({
  filterKey,
  filterOptions,
  filters,
  setFilters,
  isHovered
}) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);

  const filterOption = useMemo(
    () => filterOptions.find((option) => option.key === filterKey),
    [filterOptions, filterKey]
  );

  const activeFilterCount = useMemo(() => {
    if (!filterOption) return 0;
    const filterValue = filters[filterOption.key as keyof Filters];
    if (!filterValue) return 0;
    const values = extractFilterValues(filterValue);
    return values.length;
  }, [filterOption, filters]);

  if (!filterOption) {
    return null;
  }

  const isDateFilter = filterOption.filterType.type === "date";

  const filterLabel = getFilterLabel(filterOption);

  const handleOpenChange = (open: boolean) => {
    setIsOpen(open);
  };

  const handleClose = () => {
    setIsOpen(false);
  };

  const handleClearFilter = () => {
    setFilters((prev) => {
      const newFilters = { ...prev };
      delete newFilters[filterKey as keyof Filters];
      return newFilters;
    });
  };

  return (
    <Popover open={isOpen} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <HeaderAction
          icon={<Filter />}
          className={cn(
            !isHovered && !isOpen ? "opacity-0" : "opacity-100",
            isOpen && "bg-surface-hover-strong"
          )}
          iconClassName={cn(isOpen && "!text-primary")}
          tooltipText={t("datatoolbar.filters.tooltip")}
          onClick={(e) => e.stopPropagation()}
        />
      </PopoverTrigger>
      <PopoverContent
        align="start"
        sideOffset={4}
        padding={4}
        className={cn(isDateFilter && "w-fit")}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex w-full items-center justify-between py-1 px-2">
          <Text variant="smBodySecondary">
            {t("datatoolbar.filters.columnFilter.title", {
              label: filterLabel
            })}
          </Text>
          <Button
            variant="tertiary"
            disabled={activeFilterCount === 0}
            onClick={handleClearFilter}
          >
            {t("datatoolbar.filters.clearAll")}
          </Button>
        </div>
        <FilterContent
          filterOption={filterOption}
          filters={filters}
          setFilters={setFilters}
          onClose={handleClose}
          filterLabel={filterLabel}
        />
      </PopoverContent>
    </Popover>
  );
};

export default HeaderFilterPopover;
